<template>
  <button
    :tabindex="-1"
    :type="type"
    class="hit-button inline-grid justify-center grid-flow-col items-center"
    :class="buttonClasses"
    :disabled="disabled || showSpinner"
    v-show="!hidden"
    :title="tooltip"
    :style="mergedStyles"
    @click="handleClick"
    @mousedown="handleMouseDown"
  >
    <!-- @slot Prefix icon of the button -->
    <slot name="prefixIcon">
      <hit-icon
        v-if="prefixIcon && !showSpinner"
        class="button-icon"
        :size="resolvedIconSize"
        :icon="prefixIcon"
        :color="iconColor"
        :icon-color-style="iconColorStyle"
      />
      <hit-spinner v-else-if="showSpinner" size="sm" />
    </slot>
    <span v-if="label" class="button-label">{{ label }}</span>
    <hit-icon
      v-if="suffixIcon"
      class="button-icon"
      :size="resolvedIconSize"
      :icon="suffixIcon"
      :color="iconColor"
    />
  </button>
</template>

<script lang="ts">
/**
 * A simple button component — soft pill / chip style with semantic color tones.
 */

import {HitIcon} from '../icon';
import {defineComponent, PropType} from 'vue';
import {Icon} from '../../types/icon';
import {RouterItem} from '../../types/router';
import {getHitColorProps} from '../../mixins';
import {ColourUtils} from '../../utils/colour';
import HitSpinner from '../icon/HitSpinner.vue';

/** Maps HitButton color prop → CSS custom property used as the tone. */
const COLOR_TONE_VARS: Record<string, string> = {
  accent: 'var(--color-accent)',
  danger: 'var(--color-danger)',
  success: 'var(--color-success)',
  warning: 'var(--color-warning)',
  button: 'var(--color-button)',
  'button-floating': 'var(--color-button-floating)',
  panel: 'var(--color-panel, #e8eaed)',
  input: 'var(--color-input)',
  tag: 'var(--color-tag)',
  header: 'var(--color-header)',
  sidebar: 'var(--color-sidebar)',
  table: 'var(--color-table)',
  background: 'var(--color-background)',
};

export default defineComponent({
  name: 'HitButton',
  components: {HitIcon, HitSpinner},
  props: {
    /**
     * Label of the button
     */
    label: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Disable button and display not allowed cursor
     */
    disabled: Boolean,
    /**
     * Hide button
     */
    hidden: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * tooltip displayed when a user hovers over a button
     */
    tooltip: {
      type: String as PropType<string | null>,
      required: false,
      default: null,
    },
    /**
     * Icon displayed before label
     */
    prefixIcon: {
      type: String as PropType<Icon>,
      required: false,
      default: null,
    },
    /**
     * Icon displayed after label
     */
    suffixIcon: {
      type: String as PropType<Icon>,
      required: false,
      default: null,
    },
    /**
     * Object for router push
     */
    to: {
      type: Object as PropType<RouterItem>,
      required: false,
      default: null,
    },
    /**
     * Icon size. Available values : 2xs, xs, sm, base, md, lg, xl.
     */
    iconSize: {
      type: String as PropType<
        '2xs' | 'xs' | 'sm' | 'base' | 'md' | 'lg' | 'xl'
      >,
      default: 'sm',
    },
    /**
     * Forces the background to be white if no color scheme has been selected
     */
    whiteBackground: Boolean,
    /**
     * Remove button outline when active
     */
    removeOutline: Boolean,
    /**
     * Remove button margin
     */
    removeMargin: Boolean,
    /**
     * Remove button padding
     */
    removePadding: Boolean,
    /**
     * Only removes the vertical padding and keeps the horizontal padding
     */
    removeVerticalPadding: Boolean,
    /**
     * Gap between label and icons. Available values : none, small, normal.
     */
    gapSize: {
      type: String as PropType<'none' | 'small' | 'normal'>,
      default: 'small',
      validator: (value: string) => {
        return ['none', 'small', 'normal'].indexOf(value) !== -1;
      },
    },
    /**
     * Type of the button. Available values : button, submit, reset.
     */
    type: {
      type: String as PropType<'button' | 'submit' | 'reset'>,
      default: 'button',
      validator: (value: string) => {
        return ['button', 'submit', 'reset'].indexOf(value) !== -1;
      },
    },
    preventClick: {
      type: Boolean,
      default: true,
    },
    fullWidth: {
      type: Boolean,
      required: false,
      default: false,
    },
    iconColorStyle: {
      type: Object,
      required: false,
    },
    ...getHitColorProps(),
    customBgColor: {
      type: String,
      required: false,
      default: null,
    },

    /**
     * Shows a spinner instead of the prefix icon to show the user that something is going on
     */
    showSpinner: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * This flags makes the button invisible but reserves the space for the button to avoid a jumping layout
     */
    invisible: {
      type: Boolean,
      required: false,
      default: false,
    },

    hideBorder: {
      type: Boolean,
      required: false,
      default: false,
    },

    paddingValue: {
      type: Number,
      required: false,
      default: 2,
    },

    handleMiddleClickSeparately: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * Solid filled button (strong CTA). Default is soft tinted pill.
     */
    solid: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * Visual density.
     * - `xs` = dense chips inside tables / tight toolbars
     * - `sm` = default chips
     * - `md` = header / floating actions
     */
    size: {
      type: String as PropType<'xs' | 'sm' | 'md'>,
      required: false,
      default: 'sm',
      validator: (value: string) => ['xs', 'sm', 'md'].indexOf(value) !== -1,
    },
  },
  data: function () {
    return {
      tooltipDisabled:
        this.tooltip == undefined ||
        this.tooltip == null || //@ts-ignore
        this.tooltip.trim().length == 0,
    };
  },
  computed: {
    /**
     * Soft surface buttons (no semantic tone): default grey vs floating white.
     */
    isSurfaceColor() {
      return (
        !this.color ||
        [
          'button',
          'button-floating',
          'panel',
          'input',
          'table',
          'background',
          'header',
          'sidebar',
          'sidebar-selected',
        ].includes(this.color)
      );
    },
    /**
     * Floating / white surface (e.g. action board on dark header).
     * Uses --color-button-floating so dark mode stays correct.
     */
    isFloatingSurface() {
      return (
        this.whiteBackground ||
        this.color === 'button-floating' ||
        this.color === 'panel'
      );
    },
    /** Buttons drawn on the dark app header / sidebar. */
    isOnDarkChrome() {
      return ['header', 'sidebar', 'sidebar-selected'].includes(this.color);
    },
    /**
     * Icon-only control (no label): use equal padding so the icon is centered.
     */
    isIconOnly() {
      const hasLabel = Boolean(this.label);
      const hasPrefix =
        Boolean(this.prefixIcon) || Boolean(this.$slots.prefixIcon);
      const hasSuffix = Boolean(this.suffixIcon);
      if (hasLabel) return false;
      return (hasPrefix && !hasSuffix) || (!hasPrefix && hasSuffix);
    },
    /**
     * Dense (`xs`) buttons use a smaller icon unless iconSize was set explicitly
     * to something other than the default `sm`.
     */
    resolvedIconSize() {
      if (this.size === 'xs' && this.iconSize === 'sm') {
        return 'xs';
      }
      return this.iconSize;
    },
    toneVar() {
      if (!this.color || this.isSurfaceColor) {
        return 'var(--color-accent, #00997c)';
      }
      return COLOR_TONE_VARS[this.color] || 'var(--color-accent, #00997c)';
    },
    gapClass() {
      switch (this.gapSize) {
        case 'normal':
          return 'gap-2';
        case 'small':
          return 'gap-1';
        case 'none':
        default:
          return '';
      }
    },
    iconColor() {
      // Soft pills inherit text color via CSS; keep HitIcon color unset-friendly.
      if (this.solid && this.color && !this.isSurfaceColor) {
        return 'white';
      }
      if (this.isOnDarkChrome) {
        return this.color === 'header' ? 'header' : 'sidebar';
      }
      if (this.color && !this.isSurfaceColor) {
        return this.color === 'danger'
          ? 'danger'
          : this.color === 'success'
            ? 'success'
            : this.color === 'warning'
              ? 'warning'
              : 'clickable';
      }
      return this.isFloatingSurface ? 'button-floating' : 'default';
    },
    buttonClasses() {
      const classes = [
        this.gapClass,
        'hit-button--modern',
        this.size === 'md'
          ? 'hit-button--md'
          : this.size === 'xs'
            ? 'hit-button--xs'
            : 'hit-button--sm',
      ];

      if (this.isOnDarkChrome) {
        classes.push(
          this.color === 'header'
            ? 'hit-button--surface-header'
            : 'hit-button--surface-sidebar',
        );
      } else if (this.isSurfaceColor) {
        classes.push(
          this.isFloatingSurface
            ? 'hit-button--surface-floating'
            : 'hit-button--surface-default',
        );
      } else {
        classes.push('hit-button--toned');
        if (this.solid) {
          classes.push('hit-button--solid');
        }
      }

      if (this.isIconOnly) {
        classes.push('hit-button--icon-only');
      }

      if (this.invisible) {
        classes.push('invisible');
      }

      if (!this.removeOutline && !this.removeMargin) {
        classes.push('my-0.5');
      }

      if (!this.removePadding && !this.removeVerticalPadding) {
        classes.push('hit-button--padded');
      } else if (this.removeVerticalPadding) {
        classes.push('hit-button--padded-x');
      } else if (this.removePadding) {
        classes.push('hit-button--flush');
      }

      if (this.disabled || this.showSpinner) {
        classes.push('hit-button--disabled');
      }

      if (this.fullWidth) {
        classes.push('w-full');
      }

      if (this.hideBorder) {
        classes.push('hit-button--no-border');
      }

      return classes;
    },
    /**
     * When a custom bg color is passed to the button, these styles override the
     * standard colors set via the tailwind classes like success, etc.
     */
    customColorStyles() {
      if (this.customBgColor) {
        return {
          'background-color': this.customBgColor,
          color: ColourUtils.getFontColorOnBg(this.customBgColor),
          'border-color': this.customBgColor,
          '--hit-btn-tone': this.customBgColor,
        };
      }
      return {};
    },
    mergedStyles() {
      return {
        '--hit-btn-tone': this.toneVar,
        ...this.customColorStyles,
      };
    },
  },
  methods: {
    handleClick: function ($event: Event) {
      if (this.to) {
        // @ts-ignore
        this.$router.push(this.to);
      } else {
        /**
         * When the button is clicked
         */
        if (!this.preventClick) {
          this.$emit('click', $event);
          $event.stopImmediatePropagation();
        }
      }
    },

    handleMouseDown($event: MouseEvent) {
      if (this.handleMiddleClickSeparately && $event.button === 1) {
        this.$emit('middleClick', $event);
        $event.stopImmediatePropagation();
      }
    },
  },
});
</script>

<style scoped lang="scss">
.hit-button--modern {
  --hit-btn-tone: var(--color-accent, #00997c);
  appearance: none;
  border-radius: 9999px;
  border: 0;
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.2;
  cursor: pointer;
}

.hit-button--padded {
  padding: 0.35rem 0.7rem 0.35rem 0.55rem;
}

.hit-button--padded-x {
  padding: 0.15rem 0.55rem;
}

.hit-button--flush {
  padding: 0.2rem 0.45rem;
}

/*
 * Fixed min-heights so label-only chips match icon+label chips
 * (default HitIcon sm = 1.25rem, xs = 1.125rem).
 */
/* Dense chips — table toolbars / cells (keeps row & menu height stable) */
.hit-button--xs {
  font-size: 0.75rem;
  line-height: 1.15;
  /* 0.125rem*2 padding + 1.125rem icon */
  min-height: 1.375rem;
}

.hit-button--xs.hit-button--padded {
  padding: 0.125rem 0.5rem 0.125rem 0.4rem;
}

/* Default / compact chips (secondary rows) — size unset → sm */
.hit-button--sm {
  /* 0.35rem*2 padding + 1.25rem icon */
  min-height: 1.95rem;
}

.hit-button--sm.hit-button--padded {
  padding: 0.35rem 0.7rem 0.35rem 0.55rem;
}

/*
 * Header / floating actions: slightly taller for clearer presence,
 * without a large footprint increase. Same weight as default chips.
 */
.hit-button--md {
  font-size: 0.875rem;
  font-weight: 500;
  min-height: 2.125rem;
}

.hit-button--md.hit-button--padded {
  padding: 0.45rem 0.9rem 0.45rem 0.75rem;
}

/* Icon-only: equal padding so the glyph is centered */
.hit-button--icon-only.hit-button--padded {
  padding: 0.4rem;
}

.hit-button--icon-only.hit-button--xs.hit-button--padded {
  padding: 0.2rem;
}

.hit-button--icon-only.hit-button--md.hit-button--padded {
  padding: 0.5rem;
}

.hit-button--icon-only.hit-button--flush {
  padding: 0.35rem;
}

.hit-button--icon-only.hit-button--padded-x {
  padding-left: 0.45rem;
  padding-right: 0.45rem;
}

/*
 * Default surface: soft grey chip for tables / panels.
 * Uses dedicated theme tokens (independent from --color-table-level-*).
 */
.hit-button--surface-default {
  background: var(--color-button-default, #f7f7f7);
  color: var(--text-button-default, #374151);
  box-shadow: none;
}

.hit-button--surface-default:hover:not(:disabled) {
  background: var(--color-button-default-hover, #f2f2f2);
  color: var(--hit-btn-tone);
  box-shadow: 0 4px 12px
    color-mix(in srgb, var(--hit-btn-tone) 14%, transparent);
}

/*
 * App chrome (dark header): transparent on header, light icon/label text.
 */
.hit-button--surface-header {
  background: transparent;
  color: var(--text-header, #ffffff);
  box-shadow: none;
}

.hit-button--surface-header:hover:not(:disabled) {
  background: var(--color-header-hover, #13526d);
  color: var(--text-header, #ffffff);
  box-shadow: none;
}

.hit-button--surface-sidebar {
  background: transparent;
  color: var(--text-sidebar, #ffffff);
  box-shadow: none;
}

.hit-button--surface-sidebar:hover:not(:disabled) {
  background: var(--color-sidebar-selected, rgba(255, 255, 255, 0.08));
  color: var(--text-sidebar, #ffffff);
  box-shadow: none;
}

/*
 * Floating surface: white (light) / elevated panel (dark).
 * Border + light resting shadow keep header actions readable on the page.
 */
.hit-button--surface-floating {
  background: var(--color-button-floating, #ffffff);
  color: var(--text-button-floating, #374151);
  box-shadow:
    0 1px 2px color-mix(in srgb, #000 6%, transparent),
    inset 0 0 0 1px var(--color-button-floating-border, #d1d5db);
}

.hit-button--surface-floating:hover:not(:disabled) {
  background: var(--color-button-floating-hover, #dcdbdb);
  color: var(--hit-btn-tone);
  box-shadow:
    0 4px 12px color-mix(in srgb, var(--hit-btn-tone) 14%, transparent),
    inset 0 0 0 1px color-mix(in srgb, var(--hit-btn-tone) 28%, transparent);
}

.hit-button--surface-floating.hit-button--no-border {
  box-shadow: 0 1px 2px color-mix(in srgb, #000 6%, transparent);
}

/* Semantic soft tone: accent / warning / danger / success */
.hit-button--toned {
  background: color-mix(in srgb, var(--hit-btn-tone) 14%, transparent);
  color: var(--hit-btn-tone);
  box-shadow: inset 0 0 0 1px
    color-mix(in srgb, var(--hit-btn-tone) 22%, transparent);
}

.hit-button--toned:hover:not(:disabled) {
  background: color-mix(in srgb, var(--hit-btn-tone) 22%, transparent);
  color: var(--hit-btn-tone);
  box-shadow:
    0 4px 12px color-mix(in srgb, var(--hit-btn-tone) 16%, transparent),
    inset 0 0 0 1px color-mix(in srgb, var(--hit-btn-tone) 28%, transparent);
}

.hit-button--toned.hit-button--md {
  background: color-mix(in srgb, var(--hit-btn-tone) 18%, transparent);
  box-shadow:
    0 1px 2px color-mix(in srgb, var(--hit-btn-tone) 10%, transparent),
    inset 0 0 0 1px color-mix(in srgb, var(--hit-btn-tone) 28%, transparent);
}

.hit-button--toned.hit-button--md:hover:not(:disabled) {
  box-shadow:
    0 4px 12px color-mix(in srgb, var(--hit-btn-tone) 16%, transparent),
    inset 0 0 0 1px color-mix(in srgb, var(--hit-btn-tone) 28%, transparent);
}

/* Strong CTA when solid=true */
.hit-button--solid {
  background: var(--hit-btn-tone);
  color: #fff;
}

.hit-button--solid:hover:not(:disabled) {
  filter: brightness(1.06);
  color: #fff;
  box-shadow: 0 4px 14px
    color-mix(in srgb, var(--hit-btn-tone) 28%, transparent);
}

.hit-button--modern:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--hit-btn-tone) 70%, transparent);
  outline-offset: 2px;
}

.hit-button--modern:active:not(:disabled) {
  box-shadow: none;
}

/* Keep toned border visible while pressed (overrides the flush active state). */
.hit-button--toned:active:not(:disabled) {
  box-shadow: inset 0 0 0 1px
    color-mix(in srgb, var(--hit-btn-tone) 28%, transparent);
}

.hit-button--disabled,
.hit-button--modern:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none !important;
  background: color-mix(
    in srgb,
    var(--color-button-disabled, #d1d5db) 55%,
    transparent
  ) !important;
  color: var(--text-panel-light, #9ca3af) !important;
}

.hit-button--modern :deep(.button-icon),
.hit-button--modern :deep(i),
.hit-button--modern :deep(.material-icons),
.hit-button--modern :deep(.material-symbols-outlined) {
  color: inherit !important;
}

.hit-button--modern .button-label {
  color: inherit;
}

.grabbable {
  cursor: move;
  cursor: grab;
  background-color: rgba(0, 0, 0, 0);
}

.grabbable:hover {
  background-color: rgba(0, 0, 0, 0);
}

.grabbable:active {
  cursor: grabbing;
}
</style>
